// src/pages/projects/ProjectDetailPage.tsx
import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Users } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { useProject } from '@/hooks/useProjects';
import { useProjectMembers } from '@/hooks/useProjectMembers';
import { usePermissions } from '@/hooks/usePermissions';
import { useStableAuth } from '@/hooks/useStableAuth';
import ProjectListView from '@/components/Projects/ProjectListView';
import ProjectKanbanView from '@/components/Projects/ProjectKanbanView';
import ProjectMembersSheet from '@/components/Projects/ProjectMembersSheet';

export default function ProjectDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { canAccess } = usePermissions();
  const { isPermissionsLoading } = useStableAuth();

  const { data: project, isLoading } = useProject(id);
  const { myRole } = useProjectMembers(id);

  const [showCompleted, setShowCompleted] = useState(false);
  const [membersOpen, setMembersOpen] = useState(false);
  const [activeTab, setActiveTab] = useState<'list' | 'board'>('list');

  // RBAC — aguarda permissões carregarem para evitar redirect prematuro
  useEffect(() => {
    if (!isPermissionsLoading && !canAccess('projects:view')) {
      navigate('/dashboard', { replace: true });
    }
  }, [isPermissionsLoading, canAccess, navigate]);

  if (isPermissionsLoading) return null;
  if (!canAccess('projects:view')) return null;

  if (isLoading) {
    return (
      <div className="p-6 space-y-4">
        <div className="h-7 w-48 bg-muted animate-pulse rounded" />
        <div className="h-10 w-64 bg-muted animate-pulse rounded" />
        <div className="h-96 bg-muted animate-pulse rounded" />
      </div>
    );
  }

  if (!project) {
    return (
      <div className="p-6">
        <p className="text-muted-foreground text-sm">Projeto não encontrado.</p>
        <Button
          variant="ghost"
          className="mt-4"
          onClick={() => navigate('/dashboard/projects')}
        >
          <ArrowLeft className="h-4 w-4 mr-2" />
          Voltar para projetos
        </Button>
      </div>
    );
  }

  const canEdit = myRole === 'owner' || myRole === 'editor';

  return (
    <div className="p-6">
      {/* ── Breadcrumb ─────────────────────────────────────────────────── */}
      <div className="flex items-center gap-2 mb-5 text-sm text-muted-foreground">
        <Button
          variant="ghost"
          size="sm"
          className="gap-1.5 text-muted-foreground hover:text-foreground -ml-2"
          onClick={() => navigate('/dashboard/projects')}
        >
          <ArrowLeft className="h-4 w-4" />
          Projetos
        </Button>
        <span>/</span>
        <div className="flex items-center gap-1.5 text-foreground font-medium">
          {project.color && (
            <div
              className="h-2.5 w-2.5 rounded-full flex-shrink-0"
              style={{ backgroundColor: project.color }}
            />
          )}
          <span>{project.name}</span>
        </div>
      </div>

      {/* ── Header ─────────────────────────────────────────────────────── */}
      <div className="flex items-start justify-between gap-4 mb-6">
        <div className="min-w-0">
          <h1 className="text-2xl font-semibold leading-tight truncate">
            {project.name}
          </h1>
          {project.description && (
            <p className="text-sm text-muted-foreground mt-1 line-clamp-2">
              {project.description}
            </p>
          )}
        </div>

        <div className="flex items-center gap-2 shrink-0">
          <Button
            variant="outline"
            size="sm"
            onClick={() => setMembersOpen(true)}
          >
            <Users className="h-4 w-4 mr-1.5" />
            Membros
          </Button>
        </div>
      </div>

      {/* ── Tabs + toggle ──────────────────────────────────────────────── */}
      <Tabs
        value={activeTab}
        onValueChange={(v) => setActiveTab(v as 'list' | 'board')}
      >
        <div className="flex items-center justify-between mb-4">
          <TabsList>
            <TabsTrigger value="list">Lista</TabsTrigger>
            <TabsTrigger value="board">Quadro</TabsTrigger>
          </TabsList>

          <Button
            variant="ghost"
            size="sm"
            className="text-xs text-muted-foreground"
            onClick={() => setShowCompleted((v) => !v)}
          >
            {showCompleted ? 'Ocultar concluídas' : 'Mostrar concluídas'}
          </Button>
        </div>

        <TabsContent value="list" className="mt-0">
          <ProjectListView
            projectId={id!}
            showCompleted={showCompleted}
            canEdit={canEdit}
          />
        </TabsContent>

        <TabsContent value="board" className="mt-0">
          <ProjectKanbanView
            projectId={id!}
            showCompleted={showCompleted}
            canEdit={canEdit}
          />
        </TabsContent>
      </Tabs>

      {/* ── Sheet de membros ───────────────────────────────────────────── */}
      <ProjectMembersSheet
        projectId={id!}
        open={membersOpen}
        onOpenChange={setMembersOpen}
      />
    </div>
  );
}
